import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '~/components/common/Breadcrumbs';
import CombineProducts from '~/components/product/CombineProducts';
import ProductCollection from '~/components/product/ProductCollection';

import '~/css/style.scss';

import { api_getCustomMessageByCode, api_getProductByUrl } from '~/backend/api_calls';

import { convertImageUrl_S_to_L, convertImageUrl_S_to_M } from '~/backend/helper';

import PageHeaderMeta from '~/components/common/PageHeaderMeta';

import UserSession from '~/backend/user_session';
import { sendViewContentFacebookEvent } from '~/common/facebookPixel';
import {
	calculateProductDiscount,
	calculateProductPriceBDValue,
	calculateProductPriceValue,
	calculateProductReferencePriceValue,
	formatAmountValue
} from '~/common/helper';
import { sendViewedProductKlaviyoEvent } from '~/common/klaviyo';
import { sendViewContentTiktokEvent } from '~/common/tiktokPixel.js';
import ProductsPersonalised from '~/components/common/ProductsPersonalised.js';
import AddToCart from '~/components/product/AddToCart.js';

import { sendViewItemGTMEvent } from '~/common/gtm.js';

const Product = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	let { eurl } = useParams();
	const [product, setProduct] = useState({});
	const [productPictures, setProductPictures] = useState([]);
	const [shippingReturnMessage, setShippingReturnMessage] = useState('');
	const [chosenColor, setChosenColor] = useState('');
	const [price, setPrice] = useState(0);
	const [priceBD, setPriceBD] = useState(0);
	const [discount, setDiscount] = useState(0);
	const [referencePrice, setReferencePrice] = useState(0);
	const [loading, setLoading] = useState(false);

	// Runs once after init rendering + if eurl changes
	useEffect(() => {
		setLoading(true);

		api_getProductByUrl(eurl)
			.then(json => {
				setProduct(json);
				defaultProductPictures(json);
				sendViewContentFacebookEvent(json);
				sendViewContentTiktokEvent(json);
				sendViewedProductKlaviyoEvent(UserSession.getEmail(), json);
				sendViewItemGTMEvent(json);
				setLoading(false);
			})
			.catch(error => {
				console.error(error);
				setLoading(false);
				navigate('/notfound');
			});

		api_getCustomMessageByCode('shipping-and-returns')
			.then(json => {
				setShippingReturnMessage(json.bd);
			})
			.catch(error => console.error(error));
	}, [eurl]);

	useEffect(() => {
		if (!product) return;

		let dimensionId = 0;

		if (chosenColor) {
			dimensionId = product.dimensions.find(x => x.cval == chosenColor)?.id;
		}

		let price = calculateProductPriceValue(product, dimensionId);
		let priceBD = calculateProductPriceBDValue(product, dimensionId);
		let referencePrice = calculateProductReferencePriceValue(product, dimensionId);
		let discount = calculateProductDiscount(product, dimensionId);

		setPrice(price);
		setPriceBD(priceBD);
		setDiscount(discount);
		setReferencePrice(referencePrice);
	}, [product, chosenColor]);

	const defaultProductPictures = product => {
		let imgArray = [];

		if (product.img1 !== undefined) imgArray.push(product.img1);
		if (product.img2 !== undefined) imgArray.push(product.img2);
		if (product.img3 !== undefined) imgArray.push(product.img3);
		if (product.img4 !== undefined) imgArray.push(product.img4);
		if (product.img5 !== undefined) imgArray.push(product.img5);
		if (product.img6 !== undefined) imgArray.push(product.img6);
		if (product.img7 !== undefined) imgArray.push(product.img7);

		setProductPictures(imgArray);
	};

	useEffect(() => {
		var image_control = window.document.getElementById('images-control-overlay');
		image_control.addEventListener('wheel', handleScroll, { passive: false });

		return () => window.removeEventListener('wheel', handleScroll);
	}, []);

	const handleScroll = event => {
		event.preventDefault();
		var image_viewer = window.document.getElementById('image-viewer');
		let height = image_viewer.offsetHeight + 5;
		if (event.deltaY < 0) {
			image_viewer.scrollTo({ top: image_viewer.scrollTop - height });
		} else if (event.deltaY > 0) {
			image_viewer.scrollTo({ top: image_viewer.scrollTop + height });
		}
	};

	const thumbClick = event => {
		let current = event.currentTarget;
		let number = current.className.split(' ')[1].replace('img-', '');
		let int = parseInt(number) - 1;
		let height = window.document.getElementById('image-viewer').offsetHeight + 5;
		window.document.querySelector('.thumb-item.active').classList.remove('active');
		current.classList.add('active');
		window.document.getElementById('image-viewer').scrollTo({ top: int * height });
	};

	const AccordionToggle = event => {
		let current = event.currentTarget;
		current.classList.toggle('active');

		var panel = current.nextElementSibling;
		if (panel.style.display === 'block') {
			panel.style.display = 'none';
		} else {
			panel.style.display = 'block';
		}
	};

	const showPrice = () => {
		const priceString = formatAmountValue(price);
		let percentageFromReference = 0;
		let percentageFromReferenceString = '';
		if (price < referencePrice) {
			percentageFromReference = Math.floor((1 - price / referencePrice) * 100);
			if (percentageFromReference > 0) {
				percentageFromReferenceString = `(-${percentageFromReference}%)`;
			}
		} else {
			percentageFromReference = Math.ceil((1 - referencePrice / price) * 100);
			if (percentageFromReference > 0) {
				percentageFromReferenceString = `(+${percentageFromReference}%)`;
			}
		}

		if (priceBD <= 0) {
			return (
				<>
					<div className="price-now">{priceString}</div>
					{referencePrice !== 0 && (
						<div className="price-month">
							{t('30_days_best_price')} {formatAmountValue(referencePrice)} {percentageFromReferenceString}
						</div>
					)}
				</>
			);
		}

		// const percentageFromBD = Math.floor((1 - price / priceBD) * 100);
		// let percentageFromBDString = '';
		// if (percentageFromBD > 0) {
		// 	percentageFromBDString = `(-${percentageFromBD}%)`;
		// }

		const referencePriceString = formatAmountValue(referencePrice);
		// const priceBDString = formatAmountValue(priceBD);

		return (
			<>
				<div className="price-now">{priceString}</div>
				{referencePrice !== 0 && (
					<div className="price-month">
						{t('30_days_best_price')} {referencePriceString} {percentageFromReferenceString}
					</div>
				)}
				{/* <div className="price-bd">
					{t('start_price')} {priceBDString}
					<span>{percentageFromBDString}</span>
				</div> */}
			</>
		);
	};

	const showDiscount = () => {
		return (
			<>
				<div className="discount-container">
					<span className="discount-tag">Top Price</span>
				</div>
			</>
		);
	};

	return (
		<>
			<PageHeaderMeta
				page_title={product.nm}
				meta_keywords={product.mk}
				meta_description={product.md}
				image={product.img1}
				type={'product'}
				product_price={calculateProductPriceValue(product)}
			/>
			<div id="main-wrapper">
				<main id="product">
					<div className="content-wrapper top-wrapper">
						<div className="images-area">
							<div className="thumb-column">
								{productPictures.map((picture, index) => (
									<div className={`thumb-item img-${index + 1} ${index === 0 ? 'active' : ''}`} onClick={thumbClick} key={index}>
										<img src={convertImageUrl_S_to_M(picture)} alt={product.nm + `-thumbPicture-${index + 1}`} />
									</div>
								))}
							</div>
							<div id="images-control-overlay">
								{discount > 0 && showDiscount()}
								<div className="images-column" id="image-viewer">
									{productPictures.map((picture, index) => (
										<div className={`image-item img-${index + 1}`} key={index}>
											<img src={convertImageUrl_S_to_L(picture)} alt={product.nm + `-mainImage-${index + 1}`} />
										</div>
									))}
								</div>
							</div>
						</div>

						<div className="product-info">
							<Breadcrumbs breadcrumbs={product.breadcrumbs} currentName={product.nm} />

							<h1 className="product-title">{product.nm}</h1>

							<div className="product-code">#{product.cd}</div>

							{!loading && <div className="price">{showPrice()}</div>}
							{product.id !== undefined && (
								<AddToCart
									product={product}
									setProductPictures={setProductPictures}
									defaultProductPictures={defaultProductPictures}
									setChosenColorParent={setChosenColor}
								/>
							)}
							<div className="description-section">
								<div className="drop-item">
									<span className="drop-title" onClick={AccordionToggle}>
										{t('product_description')}
									</span>
									<div
										className="drop-subtext"
										dangerouslySetInnerHTML={{
											__html: product.dlg
										}}></div>
								</div>
								<div className="drop-item">
									<span className="drop-title" onClick={AccordionToggle}>
										{t('product_shipping_returns')}
									</span>
									<div
										className="drop-subtext"
										dangerouslySetInnerHTML={{
											__html: shippingReturnMessage
										}}></div>
								</div>
							</div>
							{product.id !== undefined && <CombineProducts productId={product.id} />}
						</div>
					</div>
				</main>
				{product.catlst && product.catlst.length > 0 && <ProductCollection categoryId={product.catlst[0]} />}
				{product.id !== undefined && (
					<ProductsPersonalised title={t('personalized_title')} excludedProductId={product.id} categoryId={product.catlst[0]} />
				)}
			</div>
		</>
	);
};

export default Product;
